import React, { useEffect, useState } from 'react';

import { TrackedSession } from '../../../../../../types/common.types';

import useTimeOptions from '../../../../../../hooks/use-time-options/useTimeOptions';
import useToaster from '../../../../../../hooks/use-toaster/useToaster';

import { ARCHIVED, ERROR } from '../../../../../../utils/constants';
import { convertDateToTimeString, convertTimeStringToDate, generateUUID } from '../../../../../../utils/util';

import Button from '../../../../../common/button/Button';
import DatePicker from '../../../../../common/input/date-picker/DatePicker';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Modal from '../../../../../common/modal/Modal';

import './accountability-tracked-session-modal.scss';

type Props = {
  status: string;
  show: boolean;
  close: () => void;
  session?: TrackedSession;
  createSession: (session: TrackedSession) => void;
  updateSession: (session: TrackedSession) => void;
};

const AccountabilityTrackedSessionModal = ({ status, show, close, session, createSession, updateSession }: Props) => {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();
  const [startDate, setStartDate] = useState<Date>();

  const { showToaster } = useToaster();

  useEffect(() => {
    if (session) {
      setName(session.name ?? '');
      setStartTime(convertDateToTimeString(session.startTime ?? ''));
      setEndTime(convertDateToTimeString(session.endTime ?? ''));

      if (!session.startDate) return;

      setStartDate(new Date(session.startDate));
    } else {
      setName('');
      setStartTime(undefined);
      setEndTime(undefined);
      setStartDate(undefined);
    }
  }, [session, show]);

  const [startTimeOptions, endTimeOptions] = useTimeOptions(startTime, endTime);

  const resetFields = () => {
    setName('');
    setStartTime(undefined);
    setEndTime(undefined);
    setStartDate(undefined);
  };

  const closeModal = () => {
    close();
    resetFields();
  };

  const handleSaveClick = () => {
    if (!name) {
      showToaster(ERROR, 'Please enter a session name');
      return;
    }

    if (!startDate) {
      showToaster(ERROR, 'Please select a date');
      return;
    }

    if (!startTime) {
      showToaster(ERROR, 'Please select a start time');
      return;
    }

    if (!endTime) {
      showToaster(ERROR, 'Please select an end time');
      return;
    }

    if (session) {
      updateSession({
        ...session,
        startTime: convertTimeStringToDate(startTime),
        endTime: convertTimeStringToDate(endTime),
        name,
        startDate
      });
    } else {
      createSession({
        uuid: generateUUID(),
        isNew: true,
        startTime: convertTimeStringToDate(startTime),
        endTime: convertTimeStringToDate(endTime),
        checkpoints: [
          {
            uuid: generateUUID(),
            isNew: true,
            name: '',
            deviceId: '',
            checkpointPasses: [],
            manualStartTime: false,
            multiplePassesConfigured: false
          }
        ],
        name,
        startDate
      });
    }

    closeModal();
  };

  return (
    <Modal show={show}>
      <div className="accountability-tracked-session-modal">
        <div className="accountability-tracked-session-modal__header">{session ? 'Edit' : 'Add'} Tracked Session</div>
        <div className="accountability-tracked-session-modal__content">
          <div className="accountability-tracked-session-modal__session-name">
            <label htmlFor="session-name">Session Name</label>
            <TextInput
              value={name}
              onChange={({ target }) => setName(target.value)}
              name="session-name"
              disabled={status === ARCHIVED}
            />
          </div>

          <div className="accountability-tracked-session-modal__session-date">
            <div>
              <label htmlFor="date">Date</label>
              <DatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                name="date"
                disabled={status === ARCHIVED}
              />
            </div>
          </div>

          <div className="accountability-tracked-session-modal__session-time">
            <p>Session Time</p>
            <div>
              <label htmlFor="start-time">From</label>
              <Dropdown
                value={startTime ? { label: startTime, value: startTime } : undefined}
                onChange={(option) => setStartTime(option.value)}
                options={startTimeOptions}
                name="start-time"
                disabled={status === ARCHIVED}
              />
              <label htmlFor="end-time">To</label>
              <Dropdown
                value={endTime ? { label: endTime, value: endTime } : undefined}
                onChange={(option) => setEndTime(option.value)}
                options={endTimeOptions}
                name="end-time"
                disabled={status === ARCHIVED}
              />
            </div>
          </div>
        </div>

        <div className="accountability-tracked-session-modal__button-container">
          <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
          {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
        </div>
      </div>
    </Modal>
  );
};

export default AccountabilityTrackedSessionModal;
